/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { TranslateService, TranslateModule, TranslateLoader, TranslateFakeLoader } from '@ngx-translate/core';
import { Observable, of, ReplaySubject, throwError } from 'rxjs';
import {
    map,
    catchError,
    mergeMap,
    tap,
    mapTo,
    distinctUntilChanged,
    retryWhen,
    delay,
    switchMap
} from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { isNil } from 'lodash';
import { SingleEventStream } from 'utils/lib/event-stream/event-streams';
import { SingleEventStreamFactoryService } from 'utils/lib/event-stream-factory/event-stream-factory.service';
import { PluginsNotificationService } from 'plugins-notification/lib/plugins-notification.service';
import { SystemInfoPluginKey } from 'plugins-notification/lib/interfaces/plugins-notification.interfaces';
import { LoggingService, Logger } from '../logging/logging.service';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';
import { RetryAttemptTelemetryData, RetryChunkLoad } from '../retry-chunk-load/retry-chunk-load.service';
import * as SIT from '../system-info-service/system-info.models';
import { SystemInfoService } from '../system-info-service/system-info.service';
import { FallbackTranslationLoader, LocaleFileHashes } from './fallback-translation-loader';
import { LocaleConfig } from './locale.config';
import {
    FALLBACK_LOCALE,
    LANGUAGE_MAPPING,
    DEFAULT_TRANSLATION_FILE,
    DEFAULT_FALLBACK_SHORT_LOCALE,
    RTLLanguageCodes
} from './locale.constants';

@Injectable({
    providedIn: 'root'
})
export class LocaleService {
    private localeChangedSubscription: SingleEventStream<string>;
    private currentLocale: string;
    private defaultLocale: string;
    private logger: Logger;
    private rtlLocaleSupported = false;
    private rtlLayoutEnabled = false;
    layoutDirectionUpdated$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private retrySubject: ReplaySubject<RetryAttemptTelemetryData> = new ReplaySubject<RetryAttemptTelemetryData>(5);
    private isSuccessEventSent = false;
    private isDirectionalityInitialized = false;
    private _collator: Intl.Collator = null;
    private _displayNames: Intl.DisplayNames = null;

    /**
     * An `Intl.Collator` object created with the current short locale
     * @property `sensitivity` = 'base'
     * @property `numeric` = true
     * Intended for sorting/searching in language-sensitive contexts
     * @requires `Intl.Collator` API to be available, otherwise `null`
     *
     * `String.localeCompare()` can be used in the absence of `Intl.Collator`.
     */
    get collator(): Intl.Collator {
        if (!this._collator && window.Intl?.Collator) {
            this._collator = new Intl.Collator(this.getShortLocale(), { sensitivity: 'base', numeric: true });
        }
        return this._collator;
    }

    /**
     * An `Intl.DisplayNames` object created with the current locale
     * useful for getting readable labels from locale codes.
     * @property `type` = 'language'
     * @property `languageDisplay` = 'standard'
     * @property `fallback` = 'none'
     * @requires `Intl.DisplayNames` API to be available, otherwise `null`
     */
    get displayNames(): Intl.DisplayNames {
        if (!this._displayNames && window.Intl?.DisplayNames) {
            this._displayNames = new Intl.DisplayNames([this.getLocale().replace('_', '-')], {
                type: 'language',
                languageDisplay: 'standard',
                fallback: 'none'
            });
        }
        return this._displayNames;
    }

    constructor(
        loggingService: LoggingService,
        private translate: TranslateService,
        private systemInfoService: SystemInfoService,
        eventStreamFactory: SingleEventStreamFactoryService,
        private config: LocaleConfig,
        private pluginsNotificationService: PluginsNotificationService,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.logger = loggingService.getLogger('LocaleService');
        this.localeChangedSubscription = eventStreamFactory.createStream<string>(this.updateLocale.bind(this));
        this.translate.setDefaultLang(DEFAULT_TRANSLATION_FILE);
    }

    private updateLocale(): Observable<string> {
        const locale$ = this.systemInfoService.getSystemInfo([SIT.SystemInfoKey.UserDefaultUILanguage]).pipe(
            map(systemInfo => {
                let defaultLocale = systemInfo.UserDefaultUILanguage;
                if (isEmpty(defaultLocale) || (this.isRTLLocale(defaultLocale) && !this.isRTLLocaleSupported)) {
                    this.logger.error(
                        `Missing default UI language. Falling back to ${FALLBACK_LOCALE} for default language.`
                    );
                    defaultLocale = FALLBACK_LOCALE;
                }

                return defaultLocale;
            })
        );

        return this.pluginsNotificationService.isPluginLoaded$(SystemInfoPluginKey).pipe(
            switchMap(isLoaded => (isLoaded ? locale$ : of(FALLBACK_LOCALE))),
            catchError(() => {
                this.logger.error(
                    `Failed to get system info. Falling back to ${this.defaultLocale} for default language.`
                );

                return of(FALLBACK_LOCALE);
            }),
            mergeMap((defaultLocale: string) => {
                let locale = defaultLocale;
                if (isNil(this.config.supportedLang.find(lang => lang === locale))) {
                    locale = this.getMappedLocale(locale);
                }
                this.defaultLocale = locale;
                return this.updateCurrentLocale();
            }),
            tap((locale: string) => {
                this.logger.info('Locale set to ' + locale);
                this._collator = null;
                this._displayNames = null;
            })
        );
    }

    public getLocaleDownloadRetryTelemetryData() {
        return this.retrySubject;
    }

    private retryOnFailure(trnlt$, locale) {
        let retryCount = 1;

        return trnlt$.pipe(
            retryWhen(errors =>
                errors.pipe(
                    switchMap(error => {
                        if (!PlatformDetectorService.isBrowserPlatform()) {
                            this.logger.error('Not a Browser Client, so retry skipped', error);
                            return throwError(error);
                        }
                        this.isSuccessEventSent = false;

                        const retryTelemetrydata: RetryAttemptTelemetryData = {
                            isSuccess: false,
                            attemptCount: retryCount,
                            error: null,
                            fileName: locale + '.json'
                        };

                        this.retrySubject.next(retryTelemetrydata);

                        if (retryCount >= RetryChunkLoad.retryAttempts) {
                            this.isSuccessEventSent = true;
                            this.logger.info('Locale JSON dowload retries expired.');
                            return throwError(error);
                        }
                        this.logger.info('Locale JSON dowload failed, trying again ', retryCount);

                        retryCount += 1;
                        return of(error);
                    }),
                    delay(retryCount * RetryChunkLoad.intervalBetweenRetries)
                )
            ),
            tap(() => {
                if (!this.isSuccessEventSent && PlatformDetectorService.isBrowserPlatform()) {
                    const retryTelemetrydata: RetryAttemptTelemetryData = {
                        isSuccess: true,
                        attemptCount: retryCount,
                        error: null,
                        fileName: locale + '.json'
                    };
                    this.retrySubject.next(retryTelemetrydata);
                    this.isSuccessEventSent = true;
                }
            })
        );
    }

    private updateCurrentLocale(): Observable<string> {
        const locale = isNil(this.currentLocale) ? this.defaultLocale : this.currentLocale;

        return this.retryOnFailure(this.translate.use(locale), locale).pipe(
            mapTo(locale),
            catchError(error => {
                // Should never come here;
                this.logger.error('Locale Json download failed with error ', error);
                return this.useMappedLocale(locale);
            })
        );
    }

    private getMappedLocale(locale: string): string {
        let mappedLocale;

        // Keeping this special case as we don't have mapping for locale zh_Hant_<>
        // (bugId 200643063 ) in the fallback excel-sheet present under MVPC-6960
        if (!isEmpty(locale) && locale.startsWith('zh_Hant')) {
            mappedLocale = 'zh_TW';
        }

        if (!isEmpty(locale) && LANGUAGE_MAPPING.get(locale)) {
            const possibleFallbackLocales: string[] = LANGUAGE_MAPPING.get(locale);
            mappedLocale = possibleFallbackLocales?.find(possibleFallbackLocale =>
                this.config.supportedLang.includes(possibleFallbackLocale)
            );
        }

        if (!mappedLocale && locale !== FALLBACK_LOCALE && !isEmpty(locale) && locale.length >= 2) {
            const possibleFallbackLocales: string[] = LANGUAGE_MAPPING.get(locale.substr(0, 2));
            mappedLocale = possibleFallbackLocales?.find(possibleFallbackLocale =>
                this.config.supportedLang.includes(possibleFallbackLocale)
            );
        }

        mappedLocale = mappedLocale || FALLBACK_LOCALE;

        this.logger.info(`Mapped locale for ${locale} is: ${mappedLocale}`);
        return mappedLocale;
    }

    private useMappedLocale(locale: string): Observable<string> {
        const mappedLocale = this.getMappedLocale(locale);
        this.isSuccessEventSent = false;

        return this.retryOnFailure(this.translate.use(mappedLocale), mappedLocale).pipe(
            mapTo(mappedLocale),
            catchError(() => {
                if (mappedLocale !== FALLBACK_LOCALE) {
                    this.logger.error(`Could not find translations for ${locale}, falling back to ${FALLBACK_LOCALE}`);
                    return this.useMappedLocale(FALLBACK_LOCALE);
                } else {
                    // This shouldn't happen on a production build. We should always package at a minimum of FALLBACK_LOCALE
                    // if you are seeing this error it means you haven't run the pack_l18n build process yet.
                    // If FALLBACK_LOCALE translations are not included this is a fatal error for the app as
                    // no strings will be translated.
                    return throwError(
                        `Could not load fallback locale ${FALLBACK_LOCALE}. Translations cannot be loaded.`
                    );
                }
            })
        );
    }

    private updateUIDirectionality() {
        this.isRTLLayoutEnabled = this.isRTLLocale(this.getLocale());
        const htmlTag = document?.getElementsByTagName('html')[0] as HTMLHtmlElement;
        htmlTag.dir = this.isRTLLayoutEnabled ? 'rtl' : 'ltr';
        const bodyTag = document?.getElementsByTagName('body')[0] as HTMLBodyElement;
        bodyTag.dir = this.isRTLLayoutEnabled ? 'rtl' : 'ltr';
        const directionClassName = this.isRTLLayoutEnabled ? 'isRtl' : 'isLtr';
        bodyTag.classList.add(directionClassName);
        this.isDirectionalityInitialized = true;
    }

    private isDirectionalityChanged() {
        if (!this.isRTLLayoutEnabled === this.isRTLLocale(this.getLocale())) {
            return true;
        }
        return false;
    }

    private updateHtmlLangAttribute() {
        this.document.documentElement.lang = this.getLocale().replace(/_/g, '-');
    }

    /**
     * Clear any active locale settings and revert back to using the system
     * provided default locale
     */
    useDefaultLocale(): Observable<string> {
        // Clear the currently selected locale service.
        // When refresh is triggered it will go out and load the default
        // locale service as specified by the system instead of one
        // requested by setLocale.
        this.currentLocale = undefined;

        return this.localeChangedSubscription.refresh();
    }

    /**
     * Sets the active locale to a new locale. This will trigger a refresh
     * and any client watching localeChanged will be updated once translations
     * have been reloaded.
     *
     * @param locale the new locale to be used in the format en-US
     */
    setLocale(locale: string): Observable<string> {
        if (isNil(this.config.supportedLang.find(lang => lang === locale))) {
            locale = this.getMappedLocale(locale);
        }

        if (locale === this.currentLocale) {
            return of(this.currentLocale);
        }

        this.currentLocale = locale;
        return this.localeChangedSubscription.refresh();
    }

    /**
     * Gets the active locale
     */
    getLocale(): string {
        return this.currentLocale ? this.currentLocale : this.defaultLocale;
    }

    /**
     * Retrieves abbreviated (shortened) locale string used in some instances
     */
    getShortLocale(): string {
        return this.getLocale() ? this.getLocale().substr(0, 2) : DEFAULT_FALLBACK_SHORT_LOCALE;
    }

    /**
     * If the current locale is changed a new locale string is emitted. Remain
     * subscribed to this to get persistent language updates.
     */
    get localeChanged(): Observable<string> {
        return this.localeChangedSubscription.get$().pipe(
            distinctUntilChanged(),
            tap(() => {
                if (!this.isDirectionalityInitialized || this.isDirectionalityChanged()) {
                    this.updateUIDirectionality();
                }
                this.updateHtmlLangAttribute();
            })
        );
    }

    get isRTLLocaleSupported(): boolean {
        return this.rtlLocaleSupported;
    }

    set isRTLLocaleSupported(rtlLocaleSupported: boolean) {
        this.rtlLocaleSupported = rtlLocaleSupported;
    }

    get isRTLLayoutEnabled(): boolean {
        return this.rtlLayoutEnabled;
    }

    set isRTLLayoutEnabled(rtlLayoutEnabled: boolean) {
        this.rtlLayoutEnabled = rtlLayoutEnabled;
        this.layoutDirectionUpdated$.next(true);
    }

    isRTLLocale(locale: string): boolean {
        return RTLLanguageCodes.includes(locale.substr(0, 2));
    }
}

/**
 * @hide
 * DO NOT CALL THIS DIRECTLY. Use importTranslateModuleForRoot
 * @param http
 * @param loggingService
 */
export function createFallbackTranslationLoader(
    http: HttpClient,
    loggingService: LoggingService,
    localeFileHashes: LocaleFileHashes
) {
    return new FallbackTranslationLoader(http, loggingService, localeFileHashes);
}

/**
 * Provides a consistent configuration for app.module.ts NgModule's imports section.
 */
export function importTranslateModuleForRoot(localeFileHashes?: LocaleFileHashes) {
    return TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: (http: HttpClient, loggingService: LoggingService) =>
                createFallbackTranslationLoader(http, loggingService, localeFileHashes),
            deps: [HttpClient, LoggingService]
        }
    });
}

/**
 * Provides a consistent configuration for app.module.ts NgModule's imports section.
 */
export function importTranslateModuleForChild() {
    // Currently just a wrapper around TranslateModule, but having all consumers of
    // localization-service use this will allow us to update everywhere if we end up
    // needing to do more like we do for root.
    return TranslateModule.forChild();
}

/**
 * Provides a consistent configuration for mocking translation service.
 */
export function importFakeTranslateModuleForRoot() {
    return TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useClass: TranslateFakeLoader }
    });
}
